import { CSSProperties, forwardRef } from 'react';

import { SizeProps } from '../../styles';
import { extractSizeProps } from '../../styles/size.styles';
import type { DefaultComponentProps } from '../../types/components';
import { extractRest } from '../../utils';
import { cn, prefixKeysStyled } from '../../utils/styles';

import { CheckboxContainer, InputElement, Label } from './Checkbox.styled';
import { CheckboxClassNames } from './constants';

export type CheckboxProps = React.InputHTMLAttributes<HTMLInputElement> &
  DefaultComponentProps &
  SizeProps & {
    hasError?: boolean;
    label?: React.ReactNode;
    whiteSpace?: CSSProperties['whiteSpace'];
  };

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (props, forwardedRef) => {
    const sizeProps = extractSizeProps(props);
    const {
      className,
      hasError,
      label,
      disabled,
      'data-testid': dataTestId,
      whiteSpace,
      onChange = () => undefined,
      ...rest
    } = extractRest(props, sizeProps);

    return (
      <CheckboxContainer
        $isDisabled={disabled}
        $whiteSpace={whiteSpace}
        className={cn(CheckboxClassNames.Root, className)}
        data-testid={dataTestId}
        onClick={event => event.stopPropagation()}
        {...prefixKeysStyled(sizeProps)}>
        <InputElement
          {...rest}
          $hasError={hasError}
          className={CheckboxClassNames.Main}
          disabled={disabled}
          ref={forwardedRef}
          type="checkbox"
          onChange={onChange}
        />

        {label && (
          <Label $hasError={hasError} className={CheckboxClassNames.Label}>
            {label}
          </Label>
        )}
      </CheckboxContainer>
    );
  },
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
